import { ONBOARDING_STATUS_QUERY, TransportError, } from "@econ-v1/rpc";
import { getSkinStyleSheet } from "../skin/skin-stylesheet.js";
import { currentDeviceLabel, loadCurrentDevice } from "./current-device.js";
import { initTheme } from "./theme-init.js";
import { NodeDialogElement } from "../components/node-dialog.js";
import { NodeEmptyStateElement } from "../components/node-empty-state.js";
import { NodeErrorElement } from "../components/node-error.js";
import { StageLoader } from "../runtime/stage-loader.js";
import { StageRegistryStore } from "../runtime/stage-registry.js";
import { StageRouter, appNameFromPath, pathForApp } from "../runtime/stage-router.js";
import { ownerStageFromPath } from "../stages/owner-stage-routes.js";
import { HOME_STAGE_TAG, registerHomeStageElement } from "../stages/home-stage.js";
import { FRIENDS_STAGE_TAG, registerFriendsStageElement } from "../stages/friends-stage.js";
import { WALLET_STAGE_TAG, registerWalletStageElement } from "../stages/wallet-stage.js";
import { AGENTS_STAGE_TAG, registerAgentsStageElement } from "../stages/agents-stage.js";
import { PROFILE_STAGE_TAG, registerProfileStageElement } from "../stages/profile-stage.js";
import { WAVES_STAGE_TAG, registerWavesStageElement } from "../stages/waves-stage.js";
import { NodeConnectivityBadgeElement } from "./connectivity-badge.js";
import { NodeSyncIndicatorElement } from "./sync-indicator.js";
import { NodeSyncCenterElement } from "./sync-center.js";
import { NodeConflictDialogElement } from "./conflict-dialog.js";
import { NodeStorageRecoveryElement } from "./storage-recovery.js";
import { NodeShellTopbarElement } from "./topbar.js";
import { NodeShellMobileNavElement } from "./mobile-nav.js";
import { NodeShellPmenuElement } from "./pmenu.js";
import { NodeShellFooterElement } from "./footer.js";
import { NodeWalletStatusElement } from "./wallet-status.js";
import { NodeDeviceListElement } from "./pairing/device-list.js";
import { NodePairingGateElementImpl } from "./pairing/pairing-gate.js";
import { NodePairingViewElement } from "./pairing/pairing-view.js";
import { NodeMigrationViewElementImpl } from "./migration-view.js";
import { registerAssistantFabElement } from "./assistant-fab.js";
import { INSTALL_LANDING_TAG, NodeInstallLandingElementImpl } from "./install/install-landing.js";
import { registerOnboardingElements } from "./onboarding/onboarding-gate.js";
/**
 * Custom-element tag for each real, kernel-bound "owner stage" (home/wallet/friends/agents/
 * profile — `SkinnedOwnerStageElement` subclasses). Populated incrementally, one stage per
 * task; an `OwnerStageName` missing from this map still resolves via `ownerStageFromPath`
 * but falls through to the generic `StageLoader` path below, same as any other installed
 * stage app, until its own task registers it here.
 */
const OWNER_STAGE_TAGS = {
    home: HOME_STAGE_TAG,
    friends: FRIENDS_STAGE_TAG,
    wallet: WALLET_STAGE_TAG,
    agents: AGENTS_STAGE_TAG,
    profile: PROFILE_STAGE_TAG,
};
class NodeAppShellElementImpl extends HTMLElement {
    #activeMount;
    #client;
    /** Identity-chip label — real paired-device name once `#loadCurrentDevice` resolves,
     * `currentDeviceLabel`'s own generic fallback until then/on failure (see `current-
     * device.ts`; replaces the old hard-coded `"Node"` — debt note item 3, #1527). */
    #currentDeviceLabel = currentDeviceLabel(undefined);
    #loader;
    /** #1529 — set whenever `#renderPath` falls back to the offline stage-load state (a stage
     * whose bundle was never cached and can't be fetched offline). The `transport.state.v1`
     * liveQuery subscription below clears this and retries the SAME path automatically the
     * moment transport connectivity is restored, so the fallback self-heals instead of
     * requiring a manual reload. Cleared unconditionally at the top of every `#renderPath`
     * call so navigating away (or a fresh render of the same path) never leaves a stale
     * auto-retry pointing at content that's no longer on screen. */
    #offlineStageRoute;
    /** #1932 — re-runs `#evaluateOnboardingStatus` whenever `transport.state.v1` reports
     * `"connected"`, so a home node resolved AFTER the shell's very first (necessarily
     * synchronous-at-boot) evaluation — e.g. the #1815-C3 LAN-discovery flow's
     * persist-then-`location.reload()` — gets a second, accurate look instead of being stuck
     * with whatever the first evaluation guessed while the freshly-resolved candidate's
     * transport was still connecting. See `#evaluateOnboardingStatus`'s doc comment. */
    #onboardingTransportUnsubscribe;
    #pairingReady = false;
    #registry;
    #registryEntries = [];
    #registryUnsubscribe;
    #routeUnsubscribe;
    #router;
    #lifecycleGeneration = 0;
    #starting = false;
    #started = false;
    /** #1661 — last observed durable-storage availability, driving the degraded-mode notice. */
    #storageState;
    #storageUnsubscribe;
    /** True while the #1697 blocking recovery card owns the outlet. Guarantees the #1661
     * banner and the blocking card are never on screen together. */
    #blockingRecoveryMounted = false;
    /** The banner element is long-lived (it lives in the shadow template), so its action
     * listener is attached exactly once rather than on every state tick. */
    #bannerActionsBound = false;
    /** Epic #1507 fidelity F2 — dark-mode class subscription (`shell/theme-init.ts`).
     * Applied on every `connectedCallback` (disposing any previous handle first, so a
     * detach/reattach cycle never leaks a duplicate `matchMedia` listener) and torn down
     * in `disconnectedCallback`. */
    #themeHandle;
    #transportState;
    #transportUnsubscribe;
    #viewRequest = 0;
    connectedCallback() {
        // Must run before the shadow tree's first paint (see theme-init.ts's own doc
        // comment) — applied unconditionally on every connect, ahead of the `!this.shadowRoot`
        // guard below, since a detach/reattach must re-resolve the theme just as freshly as
        // first mount (the OS preference or the stored choice may have changed while detached).
        this.#themeHandle?.dispose();
        this.#themeHandle = initTheme(this);
        if (!this.shadowRoot) {
            const root = this.attachShadow({ mode: "open" });
            root.adoptedStyleSheets = [getSkinStyleSheet()];
            // epic #1507 fidelity fix (wallet-scroll clipping) — `<node-shell-footer>` now
            // mounts INSIDE `.main`, as a sibling AFTER the stage outlet, instead of as a
            // `.shell`-level sibling of `.main`/`.rail`. The vendored skin's own footer
            // placement rule (`.main-view>.site-footer,.main-view[data-view="dashboard"]>
            // .site-footer,.main-view[data-view="wallet"]>.site-footer{grid-row:auto;…}`,
            // `legacy-numbered.css`) makes `.site-footer` the trailing row of the ACTIVE
            // VIEW's own scrollable grid — reached only by scrolling past that view's real
            // content, never a separate always-reserved slice of the viewport. The prior DOM
            // shape (a `.shell` sibling, "F3 bug 2" fix — see `skin-stylesheet.ts`'s
            // `SHELL_UTILITY_CSS`) instead gave the footer a DEDICATED shell-grid row whose
            // auto-sized content height (~440-460px — this footer's real, faithfully-ported
            // size, confirmed against the vendored original at :8899) is subtracted from
            // `.main`'s "1fr" track on EVERY route that shows it, unconditionally — on
            // `wallet` (long, real content) that left `.main` only ~35-50% of the viewport
            // tall, so `.nfc-section`/`.sys-pair`/`.wallet-grid` sat far past the fold with no
            // visible way to reach them (the shell's own scrollbar is intentionally invisible,
            // `scrollbar-width:none`). Moving the footer here lets `SHELL_UTILITY_CSS` restore
            // the original's real per-view relationship on EVERY content route (home, wallet,
            // and any installed stage — not just wallet, which pioneered it): `.main` itself
            // becomes ONE scrollable region spanning the outlet's real content AND the footer,
            // matching the vendored original's own model (confirmed via the same :8899
            // measurement: `.main` stays full-viewport tall there regardless of the footer's
            // size, because the footer is part of the active view's scrolling flow, not a
            // competing row — see the scroll-model comment in `skin-stylesheet.ts`).
            root.innerHTML = `
        <node-pairing-gate></node-pairing-gate>
        <div class="shell" data-shell-root data-view="settings" hidden>
          <nav class="topbar" aria-label="Toolbar"><node-shell-topbar></node-shell-topbar></nav>
          <node-shell-mobile-nav></node-shell-mobile-nav>
          <aside class="rail" data-rail></aside>
          <main class="main">
            <node-storage-recovery hidden data-storage-recovery-banner></node-storage-recovery>
            <section class="main-view" data-stage-outlet tabindex="-1"></section>
            <node-shell-footer></node-shell-footer>
          </main>
          <node-shell-pmenu></node-shell-pmenu>
          <node-wallet-status></node-wallet-status>
        </div>
        <node-assistant-fab></node-assistant-fab>
      `;
            root.addEventListener("pairing-ready", () => {
                this.#pairingReady = true;
                const gate = root.querySelector("node-pairing-gate");
                if (gate)
                    gate.hidden = true;
                const shell = root.querySelector("[data-shell-root]");
                if (shell)
                    shell.hidden = false;
                void this.#start();
            });
            root.addEventListener("client-device-revoked", () => {
                void this.#returnToRevokedGate();
            });
            root.addEventListener("navigate", (event) => {
                const detail = event.detail;
                if (detail.path) {
                    this.navigate(detail.path);
                }
            });
        }
        const gate = this.shadowRoot.querySelector("node-pairing-gate");
        gate.client = this.#client;
        // #1815 integration seam — see `#shouldShowOnboarding`'s doc comment: the DEV-ONLY
        // `?onboarding=1` URL flag is checked first and short-circuits synchronously (no client
        // round trip), so the flow stays demonstrable without a live backend. Every ordinary
        // load instead awaits the real `onboarding.status.v1` lifecycle signal — see
        // `#evaluateOnboardingStatus`'s doc comment — which is what makes onboarding mount by
        // default for an unclaimed node instead of only behind the dev flag.
        if (this.#shouldShowOnboarding()) {
            this.#mountOnboardingGate("new");
        }
        else {
            void this.#evaluateOnboardingStatus();
            this.#subscribeOnboardingTransport();
        }
        if (this.#pairingReady)
            void this.#start();
    }
    /**
     * #1815 onboarding-gate DEV-ONLY override guard.
     *
     * A developer loads the shell with `?onboarding=1` in the URL to deliberately trigger the
     * onboarding flow regardless of what `onboarding.status.v1` (`#evaluateOnboardingStatus`)
     * would otherwise decide — useful for working on the flow without a live backend, or on a
     * node whose real lifecycle state doesn't happen to be `needs-onboarding` right now. That
     * query param is never set by any normal navigation, so it is never true by default.
     * Mirrors `#bindRecoveryActions`' existing `globalThis.location` reference (no new browser
     * global introduced).
     */
    #shouldShowOnboarding() {
        return /(?:^|[?&])onboarding=1(?:&|$)/u.test(globalThis.location.search);
    }
    /**
     * #1815 onboarding-status coordinator (Tasks B+C) — the real, default-live trigger that
     * replaces guessing at "unclaimed vs claimed" with the kernel's `onboarding.status.v1`
     * live query (`client/domain/src/services/onboarding-status-service.ts` derives it from
     * the browser's device-session state plus the node's public `claimed` signal). Two
     * outcomes mount the gate: `needs-onboarding` (a reachable, unclaimed node) and
     * `bootstrap-required` (no home node discovered/reachable yet — installed-from-cloud,
     * pre-discovery — see the branch below). `ready`, `needs-pairing`, an unrecognized/empty
     * row, or a failed query all leave the existing pairing/session startup path
     * (`node-pairing-gate`'s own `client-device.session.v1` / `client-device.pair.v1` flow
     * already handles both `ready` and `needs-pairing` correctly on its own) — this method
     * removes the onboarding gate if one is currently mounted (see `#unmountOnboardingGate`)
     * and otherwise leaves that path untouched, mirroring the dev-flag branch above.
     *
     * #1932: this is called more than once. `connectedCallback` fires it once, synchronously,
     * at boot — but a home node resolved by discovery moments earlier (the #1815-C3 LAN flow
     * persists the candidate, then does a full `location.reload()` to seed it as the new
     * boot-time home node, `system/pwa/src/kernel-runtime.ts`'s `resolveInitialHomeNode`) may
     * still have its transport mid-connect when this very first evaluation runs, so a
     * genuinely CLAIMED node's real `needs-pairing` status can be missed the first time and
     * reported as `bootstrap-required` instead (the service's own fail-safe for "node not
     * reached yet" — `client/domain/src/services/onboarding-status-service.ts`). Without a
     * second look, the shell would mount the onboarding/discovery gate and never leave it —
     * the exact #1932 symptom ("discovery screen loops for a claimed node"). `#connectedCallback`
     * additionally arms `#subscribeOnboardingTransport`, which re-invokes this method every
     * time `transport.state.v1` reports `"connected"`, so the SAME home node that just raced
     * ahead of this first check gets a follow-up evaluation once its transport genuinely
     * settles — at which point a claimed node correctly resolves to `needs-pairing` and
     * `#unmountOnboardingGate` hands off to the (already `client`-bound, already running)
     * pairing gate underneath.
     *
     * A stale response (the shell disconnected or `#client` changed while the query was in
     * flight) is dropped rather than acted on.
     */
    async #evaluateOnboardingStatus() {
        const client = this.#client;
        if (!client)
            return;
        let rows;
        try {
            rows = await client.query(ONBOARDING_STATUS_QUERY, {});
        }
        catch {
            return;
        }
        if (!this.isConnected || this.#client !== client)
            return;
        const status = rows[0];
        if (status?.state === "needs-onboarding") {
            this.#mountOnboardingGate(status.scenario ?? "new");
        }
        else if (status?.state === "bootstrap-required") {
            // No home node discovered/reachable yet (installed-from-cloud pre-discovery):
            // mount the onboarding gate so its first screen — connect-discovery's manual
            // node-address entry — lets the user point the PWA at their node. After a node
            // is discovered, C3 persists it + reloads, and this re-evaluates to the real
            // needs-onboarding / needs-pairing state (see `#subscribeOnboardingTransport` for
            // why that re-evaluation isn't left to chance).
            this.#mountOnboardingGate("new");
        }
        else {
            // `ready` / `needs-pairing` / an unrecognized or empty row: onboarding is not (or no
            // longer) required. A no-op when nothing is mounted; otherwise hands off to pairing.
            this.#unmountOnboardingGate();
        }
    }
    /**
     * #1932 companion to `#evaluateOnboardingStatus`'s doc comment — subscribes to
     * `transport.state.v1` independently of (and before) `#start()`'s own subscription, which
     * only exists once `#pairingReady`, i.e. never while a `bootstrap-required`/pre-pairing
     * node is stuck showing the onboarding gate. Every `"connected"` transition re-runs the
     * onboarding-status evaluation so a home node whose transport was still settling at boot
     * gets re-checked once it genuinely comes up. Re-subscribing replaces any prior
     * subscription (mirrors `../pairing/pairing-gate.ts`'s `set client` pattern) so a `client`
     * change never leaves two listeners active.
     */
    #subscribeOnboardingTransport() {
        const client = this.#client;
        this.#onboardingTransportUnsubscribe?.();
        this.#onboardingTransportUnsubscribe = undefined;
        if (!client)
            return;
        this.#onboardingTransportUnsubscribe = client.liveQuery("transport.state.v1", {}, (diff) => {
            const next = diff.updated[0] ?? diff.added[0];
            if (next?.status === "connected")
                void this.#evaluateOnboardingStatus();
        });
    }
    /**
     * Reverses `#mountOnboardingGate`: removes the onboarding overlay (if one is mounted) and
     * reveals the pairing gate underneath — it's already `client`-bound and running its own
     * `client-device.session.v1` / `client-device.pair.v1` flow regardless of visibility (see
     * `#mountOnboardingGate`'s doc comment), so unhiding it here is enough to hand off; no
     * separate "start pairing" call is needed. A no-op when no onboarding gate is mounted, so
     * every `#evaluateOnboardingStatus` outcome can call this unconditionally.
     */
    #unmountOnboardingGate() {
        const root = this.shadowRoot;
        if (!root)
            return;
        const onboardingGate = root.querySelector("node-onboarding-gate");
        if (!onboardingGate)
            return;
        onboardingGate.remove();
        const pairingGate = root.querySelector("node-pairing-gate");
        if (pairingGate)
            pairingGate.hidden = false;
    }
    /**
     * Mounts `<node-onboarding-gate>` (`./onboarding/onboarding-gate.ts`) as an additional
     * shell-root surface, wired to the same `KernelRpcClient` the pairing gate uses, and
     * seeded with the given starting scenario (`"new"` for the dev-flag override and for every
     * real `needs-onboarding` status today — see `OnboardingLifecycleStatus.scenario`'s doc
     * comment for why only `"new"` exists so far). Idempotent: a second call while the gate is
     * already mounted is a no-op.
     *
     * `<node-pairing-gate>` stays mounted underneath (additive, not a replacement — see the
     * class-level doc + the existing app-shell tests for why), but its own `session`/`pair`
     * command flow keeps running regardless of onboarding, and for a `bootstrap-required` node
     * (no home node yet) that flow has nothing to reach and settles into an error card
     * ("Pairing could not continue"). Left alone, that card renders underneath the discovery
     * gate's overlay instead of behind nothing. `gate.hidden = true` here suppresses ONLY the
     * display — the pairing logic itself is untouched, exactly like the existing `pairing-
     * ready` handler above does once real pairing succeeds. There is nothing to restore: once
     * onboarding completes, `[data-shell-root]` becomes the visible surface (below) and the
     * pairing gate has no further reason to reappear on this path — it's only ever unhidden
     * again by `#returnToRevokedGate`'s own unrelated revocation flow.
     */
    #mountOnboardingGate(scenario) {
        const root = this.shadowRoot;
        if (!root || root.querySelector("node-onboarding-gate"))
            return;
        const pairingGate = root.querySelector("node-pairing-gate");
        if (pairingGate)
            pairingGate.hidden = true;
        const onboardingGate = document.createElement("node-onboarding-gate");
        onboardingGate.scenario = scenario;
        onboardingGate.client = this.#client;
        onboardingGate.addEventListener("onboarding-complete", () => {
            onboardingGate.remove();
            // #1932: onboarding just finished via its own local completion signal — stop
            // re-evaluating `onboarding.status.v1` on every future transport reconnect. Without
            // this, a stale/lagging server response arriving after completion could otherwise
            // race `#mountOnboardingGate` back in right after the user finished.
            this.#onboardingTransportUnsubscribe?.();
            this.#onboardingTransportUnsubscribe = undefined;
            this.#pairingReady = true;
            const shell = root.querySelector("[data-shell-root]");
            if (shell)
                shell.hidden = false;
            void this.#start();
        });
        root.prepend(onboardingGate);
    }
    disconnectedCallback() {
        this.#themeHandle?.dispose();
        this.#themeHandle = undefined;
        this.#onboardingTransportUnsubscribe?.();
        this.#onboardingTransportUnsubscribe = undefined;
        void this.#stop();
    }
    set client(value) {
        this.#client = value;
        const gate = this.shadowRoot?.querySelector("node-pairing-gate");
        if (gate)
            gate.client = value;
        const onboardingGate = this.shadowRoot?.querySelector("node-onboarding-gate");
        if (onboardingGate)
            onboardingGate.client = value;
        if (this.isConnected) {
            void this.#start();
        }
    }
    get client() {
        return this.#client;
    }
    navigate(path) {
        this.#router?.navigate(path);
    }
    async #start() {
        if (this.#started || this.#starting || !this.#client || !this.#pairingReady) {
            return;
        }
        this.#starting = true;
        this.#blockingRecoveryMounted = false;
        const generation = ++this.#lifecycleGeneration;
        const topbar = this.shadowRoot?.querySelector("node-shell-topbar");
        if (topbar) {
            topbar.client = this.#client;
        }
        const walletStatus = this.shadowRoot?.querySelector("node-wallet-status");
        if (walletStatus) {
            walletStatus.client = this.#client;
        }
        // #1661: observe durable-storage availability BEFORE the registry/transport work
        // below, and outside its try/catch. If this browser cannot provide persistent storage
        // (Safari Private Browsing), the shell still starts — the kernel's transport tier is
        // independent of the SQLite cache — but the user must be told precisely what is and
        // is not available rather than being left to infer it from empty views.
        await this.#observeStorageState(generation);
        if (!this.#isCurrentGeneration(generation)) {
            return;
        }
        const registry = new StageRegistryStore(this.#client);
        const loader = new StageLoader({ client: this.#client, registry });
        const registryUnsubscribe = registry.subscribe((entries) => {
            if (!this.#isCurrentGeneration(generation)) {
                return;
            }
            const previousEntries = this.#registryEntries;
            this.#registryEntries = entries;
            this.#renderChrome();
            this.#reconcileActiveStage(previousEntries);
        });
        let transportUnsubscribe;
        let routeUnsubscribe;
        let initialized = false;
        this.#registry = registry;
        this.#loader = loader;
        this.#registryUnsubscribe = registryUnsubscribe;
        try {
            await registry.start();
            if (!this.#isCurrentGeneration(generation)) {
                return;
            }
            this.#registryEntries = registry.entries();
            this.#transportState = await this.#loadTransportState();
            if (!this.#isCurrentGeneration(generation)) {
                return;
            }
            // #1527 item 3: resolve the real identity-chip label once, at startup — a resolution
            // failure (offline, host command not registered yet) falls back to `currentDeviceLabel`'s
            // own generic label via `loadCurrentDevice`'s internal catch, never throws here.
            this.#currentDeviceLabel = currentDeviceLabel(await loadCurrentDevice(this.#client));
            if (!this.#isCurrentGeneration(generation)) {
                return;
            }
            transportUnsubscribe = this.#client.liveQuery("transport.state.v1", {}, (diff) => {
                if (!this.#isCurrentGeneration(generation)) {
                    return;
                }
                const next = diff.updated[0] ?? diff.added[0];
                if (next) {
                    this.#transportState = next;
                    this.#renderChrome();
                    // #1529 auto-recovery: a stage that failed to open offline (never cached — see
                    // `#offlineStageRoute`'s doc comment) becomes fetchable again the instant transport
                    // reconnects. Retry the exact path that failed rather than waiting on a manual
                    // reload/Retry click.
                    if (next.status === "connected" && this.#offlineStageRoute !== undefined) {
                        const retry = this.#offlineStageRoute;
                        this.#offlineStageRoute = undefined;
                        void this.#renderPath(retry.path, retry.generation);
                    }
                }
            });
            this.#router = new StageRouter((path) => {
                void this.#renderPath(path, generation);
            });
            routeUnsubscribe = this.#router.start();
            if (!this.#isCurrentGeneration(generation)) {
                return;
            }
            this.#transportUnsubscribe = transportUnsubscribe;
            this.#routeUnsubscribe = routeUnsubscribe;
            this.#started = true;
            initialized = true;
            this.#renderChrome();
            this.dispatchEvent(new Event("node-shell-ready"));
        }
        catch (error) {
            if (this.#isCurrentGeneration(generation)) {
                this.#started = false;
                await this.#renderStartupFailure(error, generation);
            }
        }
        finally {
            if (!initialized || !this.#isCurrentGeneration(generation)) {
                registryUnsubscribe();
                transportUnsubscribe?.();
                routeUnsubscribe?.();
                registry.stop();
            }
            if (generation === this.#lifecycleGeneration) {
                this.#starting = false;
            }
        }
    }
    /**
     * Subscribes the degraded-mode notice to `client-storage.state.v1` — a live query the
     * kernel declares `requiresDurableStorage: false` precisely so it stays observable when
     * durable storage is the thing that is missing (#1661).
     *
     * Never throws: an unavailable status feed must not take the shell down with it, so a
     * failure here leaves the notice hidden and the rest of startup untouched.
     */
    async #observeStorageState(generation) {
        const client = this.#client;
        if (!client) {
            return;
        }
        const apply = (next) => {
            if (!this.#isCurrentGeneration(generation) || next === undefined) {
                return;
            }
            const previous = this.#storageState?.status;
            this.#storageState = next;
            this.#syncStorageBanner();
            // Storage came back (a successful Retry, or a slow worker that finally initialized):
            // re-render the current route so the views that were refused durable data ask again.
            if (previous === "recovery-required" && next.status === "ready" && this.#started) {
                void this.#renderPath(this.#router?.currentPath() ?? "/settings", generation);
            }
        };
        try {
            const rows = await client.query("client-storage.state.v1", {});
            apply(rows[0]);
            this.#storageUnsubscribe = client.liveQuery("client-storage.state.v1", {}, (diff) => apply(diff.updated[0] ?? diff.added[0]));
        }
        catch {
            // Diagnostics, never a precondition — the shell continues without the notice.
        }
    }
    /**
     * Keeps the in-shell banner in step with `#storageState`.
     *
     * The banner is the `banner` variant of the SAME `<node-storage-recovery>` component
     * the blocking #1697 surface uses — same copy set, same action contract, same retry
     * handler. Only placement differs, and the two are mutually exclusive by construction:
     * the banner shows only while the shell is running, and `#renderStorageRecovery` hides
     * it before mounting the blocking card (see `#hideStorageBanner`).
     */
    #syncStorageBanner() {
        const banner = this.#storageBanner();
        if (!banner) {
            return;
        }
        const state = this.#storageState;
        const show = state?.status === "recovery-required" && this.#blockingRecoveryMounted === false;
        banner.hidden = !show;
        if (!show) {
            banner.replaceChildren();
            return;
        }
        if (!this.#bannerActionsBound) {
            this.#bindRecoveryActions(banner);
            this.#bannerActionsBound = true;
        }
        banner.variant = "banner";
        banner.state = state;
    }
    /** Scoped to `[data-storage-recovery-banner]` on purpose: the blocking card is the SAME
     * tag mounted in the outlet of the SAME shadow root, so a bare tag selector would find
     * whichever came first in document order and drive the wrong element. */
    #storageBanner() {
        return this.shadowRoot?.querySelector("node-storage-recovery[data-storage-recovery-banner]");
    }
    #hideStorageBanner() {
        const banner = this.#storageBanner();
        if (banner) {
            banner.hidden = true;
            banner.replaceChildren();
        }
    }
    /**
     * Single handler for the `storage-recovery-action` contract, shared by BOTH placements
     * (#1697's blocking card and #1661's banner). Non-destructive by scope decision: retry
     * re-runs storage initialization, reload restarts the document. The kernel's
     * destructive `client-storage.reset.v1` stays deliberately unwired — local client
     * storage is wallet-adjacent state and a reset path needs its own product decision.
     */
    #bindRecoveryActions(recovery) {
        recovery.addEventListener("storage-recovery-action", (event) => {
            const action = event.detail.action;
            if (action === "reload") {
                globalThis.location.reload();
                return;
            }
            if (action === "retry") {
                void this.#retryStorageRecovery(recovery);
            }
        });
    }
    async #stop() {
        this.#lifecycleGeneration += 1;
        this.#storageUnsubscribe?.();
        this.#storageUnsubscribe = undefined;
        this.#routeUnsubscribe?.();
        this.#routeUnsubscribe = undefined;
        this.#transportUnsubscribe?.();
        this.#transportUnsubscribe = undefined;
        this.#registryUnsubscribe?.();
        this.#registryUnsubscribe = undefined;
        this.#registry?.stop();
        this.#registry = undefined;
        this.#loader = undefined;
        this.#router = undefined;
        const topbar = this.shadowRoot?.querySelector("node-shell-topbar");
        if (topbar) {
            topbar.client = undefined;
        }
        const walletStatus = this.shadowRoot?.querySelector("node-wallet-status");
        if (walletStatus) {
            walletStatus.client = undefined;
        }
        await this.#unmountActive();
        this.#started = false;
        this.#starting = false;
    }
    async #returnToRevokedGate() {
        await this.#stop();
        this.#pairingReady = false;
        const shell = this.shadowRoot?.querySelector("[data-shell-root]");
        if (shell)
            shell.hidden = true;
        const gate = this.shadowRoot?.querySelector("node-pairing-gate");
        if (gate) {
            gate.hidden = false;
            gate.showRevoked();
        }
    }
    async #loadTransportState() {
        if (!this.#client) {
            return undefined;
        }
        const states = await this.#client.query("transport.state.v1", {});
        return states[0];
    }
    #reconcileActiveStage(previousEntries) {
        const currentPath = this.#router?.currentPath();
        const appName = currentPath ? appNameFromPath(currentPath) : undefined;
        if (!appName) {
            return;
        }
        const previous = previousEntries.find((entry) => entry.appName === appName);
        const next = this.#registryEntries.find((entry) => entry.appName === appName);
        if (!next) {
            const fallback = this.#registryEntries[0]?.appName;
            this.#router?.navigate(fallback ? pathForApp(fallback) : "/settings", { replace: true });
            return;
        }
        if (previous === undefined || previous.manifestSha256 !== next.manifestSha256) {
            void this.#renderPath(currentPath, this.#lifecycleGeneration);
        }
    }
    #renderChrome() {
        const currentPath = this.#router?.currentPath() ?? "/settings";
        const stageNavItems = navItems(this.#registryEntries);
        const topbar = this.shadowRoot?.querySelector("node-shell-topbar");
        if (topbar) {
            topbar.state = this.#transportState;
            topbar.currentPath = currentPath;
            // #1527: mobile `.tb-center` gets the same registry-installed set `.pmenu`'s
            // "Stages + more" section shows, so mobile nav can reach an installed stage app too
            // (see topbar.ts's class doc for the full rationale).
            topbar.items = stageNavItems;
            // #1527 item 3: real device label instead of the hard-coded "Node" placeholder.
            topbar.currentDeviceName = this.#currentDeviceLabel;
        }
        const pmenu = this.shadowRoot?.querySelector("node-shell-pmenu");
        if (pmenu) {
            pmenu.items = stageNavItems;
            pmenu.currentPath = currentPath;
            pmenu.currentDeviceName = this.#currentDeviceLabel;
        }
        const mobileNav = this.shadowRoot?.querySelector("node-shell-mobile-nav");
        if (mobileNav) {
            mobileNav.items = stageNavItems;
            mobileNav.currentPath = currentPath;
        }
        const shell = this.shadowRoot?.querySelector("[data-shell-root]");
        if (shell) {
            shell.dataset.view = appNameFromPath(currentPath) ?? "waves";
        }
    }
    async #renderPath(path, generation = this.#lifecycleGeneration) {
        if (!this.#isCurrentGeneration(generation)) {
            return;
        }
        const requestId = ++this.#viewRequest;
        // #1529: any in-flight offline-retry intent is for whatever path was on screen before
        // this render — always stale by the time a new `#renderPath` runs (manual navigation or
        // this very auto-retry consuming it), so drop it and let the code below re-set it fresh
        // only if THIS attempt also lands on the offline stage-load fallback.
        this.#offlineStageRoute = undefined;
        this.#renderChrome();
        const outlet = this.#outlet();
        delete outlet.dataset.startupError;
        // epic #1507 fidelity F3 bug 2 root cause: reset any previous owner stage's legacy
        // view-name marker before deciding whether this render needs one — the fallback/ownerStage
        // branches below set their own fresh value ("waves"/one of the 5 owner names) on every
        // render, so a generic StageLoader-mounted app must never carry a stale one forward, or the
        // vendored per-view CSS scoped to ".main-view[data-view=\"…\"]" would keep matching content
        // it was never authored for.
        delete outlet.dataset.view;
        const appName = appNameFromPath(path);
        if (!appName) {
            await this.#unmountActive();
            if (!this.#isRenderCurrent(requestId, generation)) {
                return;
            }
            outlet.replaceChildren();
            // epic #1507 fidelity F6: this is the router's own "no matching app" fallback — every
            // unmatched path (root "/", the historical "/settings" placeholder, any bogus path;
            // `normalizePath` in `runtime/stage-router.ts` folds them all to the same value) now
            // renders the ORIGINAL skin's real default landing view instead of the invented
            // "Settings"/"No stages installed" placeholders this branch used to show — matching
            // `<body data-view="waves">` being the source's own boot default (`index.html`).
            // Device management moved to its real permanent home (the "profile" owner stage,
            // `profile-stage.ts`'s own "Your devices" section) once this task landed; `<node-
            // device-list>` itself stays registered below for that stage's own use.
            outlet.dataset.view = "waves";
            const stage = document.createElement(WAVES_STAGE_TAG);
            outlet.append(stage);
            this.#activeMount = {
                unmount: async () => {
                    stage.remove();
                },
            };
            outlet.focus();
            return;
        }
        const ownerStage = ownerStageFromPath(path);
        const ownerStageTag = ownerStage ? OWNER_STAGE_TAGS[ownerStage] : undefined;
        if (ownerStageTag && this.#client) {
            const client = this.#client;
            await this.#unmountActive();
            if (!this.#isRenderCurrent(requestId, generation)) {
                return;
            }
            outlet.replaceChildren();
            // epic #1507 fidelity F3 bug 2 root cause: the vendored per-view CSS
            // (".main-view[data-view=\"agents\"] .cg-sidebar{…}", ".main-view[data-view=
            // \"friends\"]>.msg-hd{…}", ".main-view[data-view=\"profile\"] .did-card-mini{…}",
            // etc. — `skin/stages-css/*.css`) is scoped to the LEGACY view name on the outlet
            // element itself, not just on `.shell` (which `#renderChrome()` already sets,
            // above). Without this, every one of those rules is dead: the outlet never carries
            // a `data-view` attribute at all, so e.g. the agents stage's whole 2-column
            // sidebar/main grid never applies and its unstyled `.cg-sb-search` icon `<svg>`
            // (no explicit width/height, meant to be constrained to 16px by the now-dead rule)
            // balloons to fill the outlet instead — the true cause of the "agents' glyph is cut
            // in half" bug this task fixes. `ownerStage` is always one of the 5 fixed
            // `OWNER_STAGE_NAMES`, 4 of which (agents/friends/wallet/profile) match a real
            // legacy view name 1:1 ("home" doesn't — the source calls it "dashboard" — so this
            // is a no-op for home, matching its current, unaffected rendering).
            outlet.dataset.view = ownerStage;
            const stage = document.createElement(ownerStageTag);
            stage.configure({ client });
            outlet.append(stage);
            this.#activeMount = {
                unmount: async () => {
                    stage.remove();
                },
            };
            this.#focusOutlet();
            return;
        }
        if (!this.#loader) {
            return;
        }
        await this.#unmountActive();
        if (!this.#isRenderCurrent(requestId, generation)) {
            return;
        }
        try {
            const mount = await this.#loader.load(appName, outlet, (nextPath) => {
                this.#router?.navigate(nextPath);
            }, () => this.#isRenderCurrent(requestId, generation));
            if (!this.#isRenderCurrent(requestId, generation)) {
                await mount.unmount();
                return;
            }
            this.#activeMount = mount;
            this.#focusOutlet();
        }
        catch (error) {
            if (!this.#isRenderCurrent(requestId, generation)) {
                return;
            }
            outlet.replaceChildren();
            if (isOfflineError(error)) {
                // #1529: a fresh install (or any device that has simply never opened this
                // particular stage) has no cached bundle for it, so opening it offline can't
                // succeed — that's expected, not a bug. Show the same skin-conformant, non-alarming
                // empty-state the startup path already uses for offline (`#renderStartupFallback`)
                // instead of the generic red-bordered `<node-error>`, and arm the auto-retry (see
                // `#offlineStageRoute`) so it opens on its own once connectivity returns; the Retry
                // button covers the case where transport state hasn't caught up yet.
                this.#offlineStageRoute = { generation, path };
                const empty = document.createElement("node-empty-state");
                empty.content = {
                    heading: "Stage not available offline",
                    message: "This stage hasn't been opened on this device yet, so it needs a connection " +
                        "to load for the first time. It will open automatically once you're back online.",
                    // #1529: rendered INSIDE the card by node-empty-state.ts now, styled as a real
                    // button — a prior version appended this as a bare outlet-level sibling, which
                    // (since the outlet's own grid stretch-aligns node-empty-state's unstyled outer
                    // tag to fill the row) landed far below the card as an orphaned, unstyled control.
                    action: {
                        label: "Retry",
                        onClick: () => {
                            void this.#renderPath(path, generation);
                        },
                    },
                };
                outlet.append(empty);
            }
            else {
                const nodeError = document.createElement("node-error");
                nodeError.message = describeStageError(appName, error);
                outlet.append(nodeError);
            }
            this.#focusOutlet();
        }
    }
    #focusOutlet() {
        this.#outlet().focus();
    }
    #outlet() {
        return this.shadowRoot?.querySelector("[data-stage-outlet]");
    }
    async #unmountActive() {
        const active = this.#activeMount;
        this.#activeMount = undefined;
        await active?.unmount();
    }
    #isCurrentGeneration(generation) {
        return this.isConnected && generation === this.#lifecycleGeneration;
    }
    #isRenderCurrent(requestId, generation) {
        return this.#isCurrentGeneration(generation) && requestId === this.#viewRequest;
    }
    /** Shared teardown for both startup-failure surfaces (`#renderStartupFallback` and
     * `#renderStorageRecovery`): drop every partially-initialized startup dependency and
     * re-render the chrome against the empty registry. */
    #resetStartupState() {
        this.#registryEntries = [];
        this.#registry = undefined;
        this.#loader = undefined;
        this.#routeUnsubscribe = undefined;
        this.#router = undefined;
        this.#registryUnsubscribe = undefined;
        this.#transportUnsubscribe = undefined;
        this.#transportState = undefined;
        this.#renderChrome();
    }
    /**
     * #1697 — route a startup failure to the right surface. The kernel's `client-storage.*`
     * commands are served OUTSIDE the normal-services readiness gate
     * (`StorageRecoveryCommandDispatcher` is the outermost command dispatcher in
     * `kernel-runtime.ts`), so this state read works precisely when every readiness-gated
     * query is being rejected with "Client storage recovery is required". Only a confirmed
     * `recovery-required` report swaps in the storage-recovery surface; any other state —
     * including a failed/unsupported state command (older kernel, dead transport) — keeps
     * the pre-existing generic fallback, so the two surfaces never compete for one
     * condition. (The pairing view's own error branch is a THIRD, disjoint state: it covers
     * pre-pairing bootstrap failures on the gate surface, which is hidden by the time this
     * code can run.)
     */
    async #renderStartupFailure(error, generation) {
        const recovery = await this.#loadStorageRecoveryState();
        if (!this.#isCurrentGeneration(generation)) {
            return;
        }
        if (recovery?.status === "recovery-required") {
            this.#renderStorageRecovery(recovery);
            return;
        }
        this.#renderStartupFallback(error);
    }
    /**
     * One state feed, two access modes.
     *
     * `#observeStorageState` establishes a `client-storage.state.v1` LIVE QUERY at the top
     * of `#start()` (#1661 made that query storage-free precisely so it stays observable
     * when storage is what is missing), so by the time a startup failure lands here the
     * cached `#storageState` is normally already authoritative and push-updated. The
     * one-shot COMMAND read remains as the fallback for the case the live query never got
     * established — `#start()` can throw before or during `#observeStorageState`, and the
     * #1697 surface must still work then. Both read the same kernel state.
     */
    async #loadStorageRecoveryState() {
        if (this.#storageState !== undefined) {
            return this.#storageState;
        }
        if (!this.#client) {
            return undefined;
        }
        try {
            const state = await this.#client.command("client-storage.state.v1", {});
            return typeof state === "object" && state !== null && typeof state.status === "string" ? state : undefined;
        }
        catch {
            return undefined;
        }
    }
    #renderStorageRecovery(state) {
        this.#resetStartupState();
        // Mutual exclusion: the blocking card owns the screen, so the banner must go. The
        // flag also stops `#syncStorageBanner` re-showing it on the next live-query tick.
        this.#blockingRecoveryMounted = true;
        this.#hideStorageBanner();
        const outlet = this.#outlet();
        outlet.dataset.startupError = "storage-recovery-required";
        delete outlet.dataset.view;
        outlet.replaceChildren();
        const recovery = document.createElement("node-storage-recovery");
        recovery.variant = "blocking";
        recovery.state = state;
        this.#bindRecoveryActions(recovery);
        outlet.append(recovery);
        this.#focusOutlet();
    }
    async #retryStorageRecovery(recovery) {
        if (!this.#client || recovery.busy) {
            return;
        }
        const generation = this.#lifecycleGeneration;
        recovery.busy = true;
        try {
            const state = await this.#client.command("client-storage.retry.v1", {});
            if (!this.#isCurrentGeneration(generation)) {
                return;
            }
            if (typeof state === "object" && state !== null && state.status === "ready") {
                void this.#start();
                return;
            }
            recovery.state = typeof state === "object" && state !== null && typeof state.status === "string"
                ? state
                : retryFailedState(recovery.state);
        }
        catch {
            if (this.#isCurrentGeneration(generation)) {
                recovery.state = retryFailedState(recovery.state);
            }
        }
        finally {
            recovery.busy = false;
        }
    }
    #renderStartupFallback(error) {
        this.#resetStartupState();
        const outlet = this.#outlet();
        outlet.dataset.startupError = startupErrorCode(error);
        outlet.replaceChildren();
        const empty = document.createElement("node-empty-state");
        const action = {
            label: "Retry",
            onClick: () => {
                void this.#start();
            },
        };
        empty.content = isOfflineError(error)
            ? {
                heading: "Offline shell",
                message: "The shell could not refresh its startup data yet. Retry once connectivity returns.",
                action,
            }
            : {
                heading: "Shell startup failed",
                message: "The shell could not initialize. Retry to request a fresh registry and transport snapshot.",
                action,
            };
        outlet.append(empty);
        // #1529: node-empty-state.ts renders the action button INSIDE its own card (see that
        // module) — tag it here so existing "retry the shell" lookups keep working.
        empty.querySelector(".node-empty-state-action").dataset.retryShell = "true";
        this.#focusOutlet();
    }
}
export function registerNodeUiElements() {
    registerOnboardingElements();
    registerHomeStageElement();
    registerFriendsStageElement();
    registerWalletStageElement();
    registerAgentsStageElement();
    registerProfileStageElement();
    registerWavesStageElement();
    define("node-app-shell", NodeAppShellElementImpl);
    registerAssistantFabElement();
    define("node-connectivity-badge", NodeConnectivityBadgeElement);
    define("node-dialog", NodeDialogElement);
    define("node-empty-state", NodeEmptyStateElement);
    define("node-error", NodeErrorElement);
    define("node-shell-topbar", NodeShellTopbarElement);
    define("node-shell-mobile-nav", NodeShellMobileNavElement);
    define("node-shell-pmenu", NodeShellPmenuElement);
    define("node-shell-footer", NodeShellFooterElement);
    define("node-wallet-status", NodeWalletStatusElement);
    define("node-sync-indicator", NodeSyncIndicatorElement);
    define("node-sync-center", NodeSyncCenterElement);
    define("node-conflict-dialog", NodeConflictDialogElement);
    define("node-storage-recovery", NodeStorageRecoveryElement);
    define("node-pairing-view", NodePairingViewElement);
    define("node-pairing-gate", NodePairingGateElementImpl);
    define("node-device-list", NodeDeviceListElement);
    define("node-migration-view", NodeMigrationViewElementImpl);
    define(INSTALL_LANDING_TAG, NodeInstallLandingElementImpl);
}
function define(name, constructor) {
    if (!customElements.get(name)) {
        customElements.define(name, constructor);
    }
}
function navItems(entries) {
    return [...entries]
        .filter((entry) => entry.ui.kind === "stage" && entry.ui.nav !== undefined)
        .sort((left, right) => (left.ui.nav.section.localeCompare(right.ui.nav.section) ||
        left.ui.nav.order - right.ui.nav.order ||
        left.ui.title.localeCompare(right.ui.title) ||
        left.appName.localeCompare(right.appName)))
        .map((entry) => ({
        label: entry.ui.title,
        path: pathForApp(entry.appName),
    }));
}
function describeStageError(appName, error) {
    if (error instanceof Error && error.message.length > 0) {
        return `Stage ${appName} could not be opened: ${error.message}`;
    }
    return `Stage ${appName} could not be opened.`;
}
function isOfflineError(error) {
    if (error instanceof TransportError) {
        return true;
    }
    if (typeof error !== "object" || error === null || !("code" in error)) {
        return false;
    }
    const code = error.code;
    return code === "connection_lost" || code === "offline" || code === "unavailable";
}
/** #1697 — shown when the retry COMMAND itself failed to run (transport-level), or
 * returned an unrecognizable payload. Preserves the previous diagnostics (code/schema/
 * generation) so the disclosure stays truthful about the underlying condition. */
function retryFailedState(previous) {
    return {
        ...(previous ?? {}),
        message: "Storage retry could not complete. Try again, or reload the app.",
        status: "recovery-required",
    };
}
function startupErrorCode(error) {
    if (typeof error === "object" && error !== null && "code" in error && typeof error.code === "string") {
        return error.code;
    }
    return "unknown";
}
export { NodeAppShellElementImpl };
//# sourceMappingURL=app-shell.js.map